<script setup>
  import { ref, computed, onErrorCaptured } from 'vue'
  import DataContainerSection from '@/components/DataContainerSection.vue';
  import ErrorBlock from '@/components/ErrorBlock.vue';
  import DataContainer from '@/components/DataContainer.vue';
  import { useBugHandler } from '@/composables/useBugHandler';
  import useSettingsGlobals from '@/composables/useSettingsGlobals';
  import ModalDeleteFavoriteContainer from '@/components/ModalDeleteFavoriteContainer.vue';
  import ModalRenameFavoriteContainer from '@/components/ModalRenameFavoriteContainer.vue';
  import ModalMoveCopyContainerToProfile from '@/components/ModalMoveCopyContainerToProfile.vue';
  import PopupMenuFavoriteItemActions from '@/components/PopupMenuFavoriteItemActions.vue';


  const { $WEB_VIEW } = useSettingsGlobals();   

  const props = defineProps({
    favoriteContainer: {
      type: Object,
      required: true
    },
    minHeight: {
      type: String,
      required: false
    },
    contentClass: {
      type: String,
      required: false
    },
    metaActions: {
      type: Boolean,
      default: true
    },
    alwaysShowMetaActions: {
      type: Boolean,
      default: false
    }
  });

  const { report } = useBugHandler();

  const container = ref(null);
  const showDeleteContainerModal = ref(false);
  const showRenameContainerModal = ref(false);
  const showMoveCopyContainerModal = ref(false);
  const moveCopyAction = ref(null);
  const error = ref(null);

  const containerId = computed(() => props.favoriteContainer.container_id);
  const containerTypeId = computed(() => props.favoriteContainer.container_type_id);
  const title = computed(() => props.favoriteContainer.container_name);

  function handleDeleteContainer() {
    showDeleteContainerModal.value = true;
  }

  function handleRenameContainer() {
    showRenameContainerModal.value = true;
  }

  function handleMoveCopyContainer(actionType) {
    moveCopyAction.value = actionType;
    showMoveCopyContainerModal.value = true;
  }

  onErrorCaptured((e) => {
    console.warn(e);

    error.value = { 
      title: `There was a problem loading the ${title.value}`,
      message: `Please try again later.`,
      errorText: e.message
    }

    report(e, { 
      component: 'FavoriteContainerSection',
      sectionId: containerId.value,
      containerTypeId: containerTypeId.value,
    });    

    return false;
  })   
</script>

<template>
    <DataContainerSection
      v-model:container="container"
      :lazy="true"
      :id="containerId"
      :minHeight="props.minHeight"
      :contentClass="props.contentClass"
    >
      <template #title>{{ title }}</template>
      <template v-if="props.metaActions && !$WEB_VIEW || props.metaActions && props.alwaysShowMetaActions" #metaActions>
        <slot name="metaActions">
          <PopupMenuFavoriteItemActions
            :itemId="containerId"
            @rename="handleRenameContainer"
            @delete="handleDeleteContainer"
            @copy="handleMoveCopyContainer('copy')"
            @move="handleMoveCopyContainer('move')"
          />
        </slot>
      </template>
      <template #content="{ isVisible }">
        <DataContainer v-if="error" style="width: 100%; height: 100%;">
          <ErrorBlock :error="error" />
        </DataContainer>
        <slot v-else name="content" :isVisible="isVisible"></slot>
      </template>
    </DataContainerSection>
    <ModalRenameFavoriteContainer
      v-if="showRenameContainerModal"
      :containerId="containerId"
      :show="showRenameContainerModal"
      @close="showRenameContainerModal = false"
    />
    <ModalDeleteFavoriteContainer
      v-if="showDeleteContainerModal"
      :containerId="containerId"
      :show="showDeleteContainerModal"
      @close="showDeleteContainerModal = false"
    />
    <ModalMoveCopyContainerToProfile
      v-if="showMoveCopyContainerModal"
      :containers="[props.favoriteContainer]"
      :action="moveCopyAction"
      :show="showMoveCopyContainerModal"
      @close="showMoveCopyContainerModal = false"
    />

</template>